import Api from "../../../https/Api";
import i18n from "../../../i18n/config";
import { CategoryInputs } from "../../../utils/AnnounceInterfaces";
import { IArticle, IArticleBlog } from "../../../utils/ArticleInterface";
import { HomeSectionsResponse } from "../BlogHome";

export class BlogService {

    private api: Api = new Api();

    async getAllPartners() {
        const response = await this.api.get("partners");
        return response.data;  // Ensure you return the data
    }

    async getAllPartnersNav() {
        const response = await this.api.get("partners/nav");
        //console.log('getAllPartnersNav raw response:', response); // Debug
        return response;  // Ensure you return the data
    }

    // Get all articles
    async getAllArticles(pagination: { page: number; size: number }) {
        const { page, size } = pagination;
        
        // Use query parameters instead of path parameters
        const response = await this.api.get("articles", { 
            page: page.toString(), 
            size: size.toString() 
        });
        
        // CORRECTED: Handle the nested response structure
        if (response && response.success === true) {
            return response.data; // This contains the nested data object with articles array
        } else {
            throw new Error('Failed to fetch articles');
        }
    }

    // Get single article by ID
    async getArticleById(id: string) {
        return this.api.simpleGet("articles/get", id);
    }

    // Get single article by slug
    async getArticleBySlug(slug: string) {
        return this.api.simpleGet("getonebyslug", slug);
    }

    // Increment likes
    async incrementLikes(articleId: string, userId: string) {
        return this.api.post(`articles/${articleId}/like`, { userId });
    }

    // Increment likes by slug
    async incrementLikesBySlug(slug: string, userId: string) {
        return this.api.post(`articles/slug/${slug}/like`, { userId });
    }

    // Decrement likes
    async decrementLikes(articleId: string, userId: string) {
        return this.api.post(`articles/${articleId}/unlike`, { userId });
    }

    // Decrement likes by slug
    async decrementLikesBySlug(slug: string, userId: string) {  
        return this.api.post(`articles/slug/${slug}/unlike`, { userId });
    }

    // Increment views by slug
    async incrementViewsBySlug(slug: string) {
        return this.api.post(`articles/slug/${slug}/view`, {});
    }

    // Create comment by slug
    async createCommentBySlug(slug: string, commentData: any) {
        return this.api.post(`articles/slug/${slug}/comments`, commentData);
    }

    // Update comment visibility by slug
    async updateCommentVisibilityBySlug(slug: string, commentId: string, status: string) {
        return this.api.put(`articles/slug/${slug}/comments/${commentId}/visibility`, { status });
    }

     // Theme related methods
     async getAllThemes() {
        return this.api.get("articlethemes");
    }

    async getThemeById(id: string) {
        return this.api.simpleGet("articlethemes/get", id);
    }

    async saveTheme(theme: CategoryInputs) {
        return this.api.post("articlethemes/create/", theme);
    }

    async deleteTheme(id: string) {
        return this.api.simpleDeleteById(`articlethemes/delete`, id);
    }

    async deleteThemes(ids: string[]) {
        const param = {
            ids: ids,
        };
        return this.api.deleteMany("articlethemes/deleteMany", param);
    }

     // get
     async getAllCategories() {
        const response = await this.api.get("articlecategories");
        return response.data;  // Ensure you return the data
    }

    async getAllCategoriesNav(lang: string = 'fr') {
        const response = await this.api.get("articlecategories/nav", { lang }); // Remove the nested params
        //console.log('getAllCategoriesNav raw response:', response); // Debug
        return response;
    }

    async getCategoryById(id: string) {
        return this.api.simpleGet("articlecategories/get", id);
    }

    async getArticlesByTheme(themeName: string): Promise<IArticle[]> {
        try {
            const response = await this.api.get(`articles/theme/${themeName}`);
            return response.data;
        } catch (error) {
            throw new Error('Error fetching articles by theme');
        }
    }

    // Frontend service method
    async getArticlesByCategory(categorySlug: string): Promise<IArticleBlog[]> {
        try {
            const response = await this.api.get(`articles/category/${categorySlug}`);

            return response.data; // Assuming API response contains `data` field
        } catch (error) {
           
            throw new Error('Error fetching articles by category');
        }
    }

    // Update function to take slug instead of _id
    async getArticlesByPartner(slug: string): Promise<IArticleBlog[]> {
        try {
            const response = await this.api.get(`articles/partner/slug/${slug}`);
            return response.data;  // Assuming API response contains `data` field
        } catch (error) {
            throw new Error('Error fetching articles by partner slug');
        }
    }

     // Method to fetch a partner by slug
     async getPartnerBySlug(slug: string): Promise<any> {
       try {
            const response= await this.api.get(`partners/slug/${slug}`);
            return response.data;  // Return the partner data
        } catch (error) {
            throw new Error('Error fetching partner by slug');
        }
    }

    async getHomeSections(): Promise<HomeSectionsResponse> {
        try {
            const response = await this.api.get('articles/home-sections');
            //console.log('Home sections response:', response.data);
            return response.data;
        } catch (error) {
            throw new Error('Error fetching home sections');
        }
    }
    
    async searchArticles(searchTerm: string, page: number = 1, limit: number = 10): Promise<{
        articles: IArticle[];
        totalCount: number;
        currentPage: number;
        totalPages: number;
    }> {
        try {
            //console.log('🔍 [Frontend Service] Sending search request:', { searchTerm, page, limit });

            // The key fix: Pass the parameters as a flat object directly as the second argument.
            const response = await this.api.get("articles/search", { // This object is the `params` argument
                q: searchTerm, 
                page, 
                limit 
            });

        //console.log('✅ [Frontend Service] Request successful.');
        //console.log('📦 [Frontend Service] Full response structure:', response);
            
            // Correctly map the actual API response structure
            const result = {
                articles: response.data.articles || response.data.data || [], // Try both structures
                totalCount: response.data.totalCount || response.data.totalelemets || 0,
                currentPage: response.data.currentPage || page,
                totalPages: response.data.totalPages || response.data.totalPage || 1
            };
            
            /*console.log('📊 [Frontend Service] Mapped results:', {
                articlesCount: result.articles.length,
                totalCount: result.totalCount,
                currentPage: result.currentPage,
                totalPages: result.totalPages
            }); */
            
            return result;
            
        } catch (error: any) {
            console.error('💥 [Frontend Service] Search request failed:');
            if (error.response) {
                console.error('🚨 Server responded with error status:', error.response.status);
                console.error('📜 Error response data:', error.response.data);
            } else if (error.request) {
                console.error('🌐 No response received from server. Request was:', error.request);
            } else {
                console.error('⚙️ Error setting up request:', error.message);
            }
            throw error;
        }
    }
}

